<template>
    <div class="py-[30px]">
        <!-- API Key Management Section -->
        <div class="flex flex-col px-[100px] text-[16px] pb-[30px] gap-y-4 border-b border-gray-600 mb-8">
            <div class="flex justify-end">
                <v-btn density="compact" variant="text" class="text-sm" @click="generateApiKey" >
                    <v-icon icon="mdi-key"></v-icon><span>Create API Key</span>
                </v-btn>
            </div>
            <div v-if="apikey" class="ml-4 p-3 w-fit gap-x-4 flex items-center rounded">
                <span>API Key:</span>
                <span class="font-mono">{{ maskApiKey(apikey) }}</span>
                <v-btn density="compact" variant="text" icon="mdi-content-copy" @click="apiCopy" color="light-green" title="Copy API Key"></v-btn>
                <v-btn icon="mdi-delete" density="compact" variant="text" color="red-lighten-1" @click="deleteApi" title="Delete API Key"></v-btn>
            </div>
             <div v-else class="ml-4 p-3 text-gray-400">
                No API Key generated yet. Click "Create API Key".
            </div>
        </div>

        <!-- API Documentation Section -->
        <div class="px-[100px]">

            <!-- Loading State -->
            <div v-if="isLoadingDocs" class="text-center py-10">
                <p>Loading documentation...</p>
                <!-- Optional: Add a spinner -->
                 <v-progress-circular indeterminate color="light-green"></v-progress-circular>
            </div>

            <!-- Error State -->
            <div v-else-if="errorLoadingDocs" class="text-red-500 bg-red-100 border border-red-400 rounded p-4">
                <p><strong>Error loading documentation:</strong></p>
                <p>{{ errorLoadingDocs }}</p>
            </div>

            <!-- Documentation Content -->
             <!-- Add prose class for nice default markdown styling with Tailwind Typography -->
            <div v-else class="prose max-w-none dark:prose-invert" v-html="renderedMarkdown"></div>
        </div>

    </div>
</template>

<script setup>
import { ref, watchEffect, onMounted, computed } from 'vue';
import copy from "clipboard-copy";
import { useApiKeyStore, useAlertStore } from "@/stores";
import { storeToRefs } from "pinia";
import { marked } from 'marked'; // Import the marked library

// --- Existing API Key Logic ---
const apikeyStore = useApiKeyStore();
const { apikey, docs } = storeToRefs(apikeyStore);
const alertStore = useAlertStore(); // Define alertStore here if not already

watchEffect(() => {
    // Initial fetch or check if key exists
    if (!apikey.value) {
        apikeyStore.getApiKey();
    }
});

const generateApiKey = async () => {
    await apikeyStore.generateApiKey();
};

const apiCopy = () => {
    if (apikey.value) {
        copyText(apikey.value);
    }
};

const deleteApi = async () => {
    // Optional: Add confirmation dialog here
    await apikeyStore.deleteApiKey();
};

const copyText = (text) => {
    copy(text);
    alertStore.success("API Key Copied");
};

const maskApiKey = (apiKey) => {
  if (!apiKey || apiKey.length < 10) return 'Invalid Key'; // Handle null/short keys
  return `${apiKey.slice(0, 5)}...${apiKey.slice(-4)}`;
};

// --- New API Documentation Logic ---
const isLoadingDocs = ref(true);
const errorLoadingDocs = ref(null); // To store any fetching errors

// Fetch markdown content when the component mounts
onMounted(async () => {
    try {
        isLoadingDocs.value = true;
        errorLoadingDocs.value = null;
        // Adjust the URL to your backend endpoint
        await apikeyStore.getApiDocs(); // Use the backend route you created


    } catch (error) {
        console.error("Error fetching documentation:", error);
        // Check if a specific message was thrown, otherwise use a generic one
        errorLoadingDocs.value = error.message || 'Could not load documentation content.';
    } finally {
        isLoadingDocs.value = false;
    }
});

// Computed property to render markdown to HTML
const renderedMarkdown = computed(() => {
    if (docs.value) {
        // Configure marked options if needed (e.g., for syntax highlighting later)
        // marked.setOptions({ ... });
        return marked.parse(docs.value, { breaks: true });
    }
    return '<p>No documentation content loaded.</p>'; // Fallback content
});

</script>

<style>
.prose pre, .prose pre code {
  background-color: #b1b3b6 !important; /* light gray */
  color: #24292e !important;            /* dark text */
  border-radius: 6px;
  font-size: 1em;
  overflow-x: auto;
}

/* Dark mode: code blocks */
.dark .prose pre, .dark .prose pre code {
  background-color: #5f6164 !important; /* dark gray */
  color: #f8f8f2 !important;            /* light text */
}
</style>